// NtfyService - Send push notifications via ntfy.sh
import { SignalPrediction } from '../store/signalPredictorStore';
import { getPreferences } from '../hooks/usePreferences';

export interface NtfyConfig {
  enabled: boolean;
  topic: string;
}

class NtfyService {
  private config: NtfyConfig = {
    enabled: false,
    topic: '',
  };

  constructor() {
    this.loadConfig();
  }

  private loadConfig() {
    try {
      const prefs = getPreferences();
      const settings = prefs.signalPredictor?.settings;
      if (settings) {
        this.config = {
          enabled: settings.ntfyEnabled ?? false,
          topic: settings.ntfyTopic ?? '',
        };
      }
    } catch (e) {
      console.error('[NtfyService] Failed to load config:', e);
    }
  }

  updateConfig(config: Partial<NtfyConfig>) {
    this.config = { ...this.config, ...config };
  }

  isConfigured(): boolean {
    return this.config.enabled && this.config.topic.trim().length > 0;
  }

  async sendAlert(prediction: SignalPrediction): Promise<boolean> {
    if (!this.isConfigured()) {
      console.log('[NtfyService] Not configured, skipping notification');
      return false;
    }

    const direction = prediction.direction || 'SIGNAL';
    const probability = Math.round(prediction.probability);
    const timeLeft = prediction.secondsToEvaluation;
    const mins = Math.floor(timeLeft / 60);
    const secs = timeLeft % 60;
    const timeStr = `${mins}:${secs.toString().padStart(2, '0')}`;

    const title = `${direction} Signal - ${probability}%`;
    const message = `Wick: ${prediction.wickPct.toFixed(1)}% | Time: ${timeStr} | ATR: ${prediction.atr.toFixed(1)}`;

    try {
      const priority = prediction.alertLevel === 'imminent' ? '5' : '4';
      const tags = direction === 'LONG'
        ? 'chart_with_upwards_trend,bell'
        : 'chart_with_downwards_trend,bell';

      const response = await fetch(`https://ntfy.sh/${this.config.topic}`, {
        method: 'POST',
        headers: {
          'Title': title,
          'Priority': priority,
          'Tags': tags,
        },
        body: message,
      });

      if (response.ok) {
        console.log(`[NtfyService] Alert sent: ${title}`);
        return true;
      } else {
        console.error(`[NtfyService] Failed to send: ${response.status} ${response.statusText}`);
        return false;
      }
    } catch (e) {
      console.error('[NtfyService] Error sending notification:', e);
      return false;
    }
  }

  async sendTakeItAlert(direction: 'LONG' | 'SHORT', confidence: number, confluence: number): Promise<boolean> {
    // TAKE IT alerts use dedicated topic
    const takeItTopic = 'utt-trading-alerts-takeit';

    const combinedScore = Math.round(confidence + confluence);
    const title = `🔥 TAKE IT - ${direction}`;
    const message = `Score: ${combinedScore} (C:${Math.round(confidence)} + CF:${Math.round(confluence)})`;

    try {
      const tags = direction === 'LONG'
        ? 'fire,chart_with_upwards_trend,moneybag'
        : 'fire,chart_with_downwards_trend,moneybag';

      const response = await fetch(`https://ntfy.sh/${takeItTopic}`, {
        method: 'POST',
        headers: {
          'Title': title,
          'Priority': '5',
          'Tags': tags,
        },
        body: message,
      });

      if (response.ok) {
        console.log(`[NtfyService] TAKE IT alert sent: ${title}`);
        return true;
      } else {
        console.error(`[NtfyService] TAKE IT failed: ${response.status} ${response.statusText}`);
        return false;
      }
    } catch (e) {
      console.error('[NtfyService] TAKE IT error:', e);
      return false;
    }
  }

  async sendTestNotification(): Promise<boolean> {
    if (!this.config.topic.trim()) {
      console.error('[NtfyService] No topic configured');
      return false;
    }

    try {
      const response = await fetch(`https://ntfy.sh/${this.config.topic}`, {
        method: 'POST',
        headers: {
          'Title': 'Test Notification',
          'Priority': '3',
          'Tags': 'white_check_mark,test_tube',
        },
        body: 'If you see this, ntfy notifications are working!',
      });

      if (response.ok) {
        console.log('[NtfyService] Test notification sent successfully');
        return true;
      } else {
        console.error(`[NtfyService] Test failed: ${response.status} ${response.statusText}`);
        return false;
      }
    } catch (e) {
      console.error('[NtfyService] Test error:', e);
      return false;
    }
  }
}

// Singleton instance
export const ntfyService = new NtfyService();
